import type React from "react";
import { useMemo } from "react";

import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { endOfDay } from "date-fns";
import { isEqual } from "lodash-es";

import { agentExecutionApi, copilotExecutionApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import {
  type AgentHistoryResponse,
  type AgentInvokeResponse,
  type CopilotInvokeRequest,
  type CopilotResourceRef,
  Environment,
  type HumanMessageContentBlocksInner,
  HumanMessageTypeEnum,
  type SessionMessagesResponse,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
  SortDirection,
  SortField,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { useCurrentUserId } from "src/store/AuthStore";

const SESSION_CACHE_KEY_PREFIX = "unified-copilot";
export const UNIFIED_COPILOT_AGENT_ID = "ffffffff-0000-4000-8000-000000000005";
const MESSAGE_PAGE_SIZE = 50;
const SESSION_HISTORY_PAGE_SIZE = 50;
const SESSION_HISTORY_FROM = new Date(0).toISOString();

const normalizeResourceRef = (ref: CopilotResourceRef): CopilotResourceRef => ({
  type: ref.type,
  id: ref.id,
  ...(ref.parent ? { parent: normalizeResourceRef(ref.parent) } : {}),
});

const normalizeHumanContentBlocks = (
  blocks: HumanMessageContentBlocksInner[] | null | undefined,
): HumanMessageContentBlocksInner[] =>
  (blocks ?? []).map((block) => {
    switch (block.type) {
      case "text":
        return { type: "text" as const, text: block.text };
      case "mention":
        return {
          type: "mention" as const,
          label: block.label,
          ref: normalizeResourceRef(block.ref),
        };
      case "file":
        return {
          type: "file" as const,
          file_id: block.file_id,
          name: block.name,
          // The optimistic block sends null while the server may echo the
          // field back as undefined — normalize so isEqual matches them.
          file_type: block.file_type ?? null,
        };
    }
  });

export const UNIFIED_COPILOT_TERMINAL_STATUSES: ReadonlySet<SessionStatus> =
  new Set([
    SessionStatus.SUCCESS,
    SessionStatus.FAILED,
    SessionStatus.CANCELLED,
  ]);

const isTerminalStatus = (status: SessionStatus | undefined): boolean =>
  status !== undefined && UNIFIED_COPILOT_TERMINAL_STATUSES.has(status);

export const unifiedCopilotSessionKeys = {
  all: (workspaceId: string | null) =>
    [SESSION_CACHE_KEY_PREFIX, workspaceId] as const,
  history: (workspaceId: string | null) =>
    [...unifiedCopilotSessionKeys.all(workspaceId), "history"] as const,
  historyList: (
    workspaceId: string | null,
    userId: string | undefined,
    to: string,
  ) => [...unifiedCopilotSessionKeys.history(workspaceId), userId, to] as const,
  messages: (workspaceId: string | null, sessionId: string | null) =>
    [
      ...unifiedCopilotSessionKeys.all(workspaceId),
      "sessions",
      sessionId,
      "messages",
    ] as const,
};

export type UnifiedCopilotMessagesPage = Omit<
  SessionMessagesResponse,
  "session"
> & {
  session?: SessionMessagesResponse["session"];
};

export type UnifiedCopilotMessagesData =
  InfiniteData<UnifiedCopilotMessagesPage>;

type UnifiedCopilotClient = {
  invoke: (request: CopilotInvokeRequest) => Promise<AgentInvokeResponse>;
  listSessions: (params: {
    cursor?: string;
    filterCreatedById: string;
    from: string;
    to: string;
    pageSize: number;
  }) => Promise<AgentHistoryResponse>;
  listSessionMessages: (params: {
    sessionId: string;
    pageSize: number;
    cursor?: string;
  }) => Promise<SessionMessagesResponse>;
  cancelSession: (params: { sessionId: string }) => Promise<void>;
};

const useUnifiedCopilotClient = (): {
  client: UnifiedCopilotClient | undefined;
  workspaceId: string | null;
} => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  const client = useMemo((): UnifiedCopilotClient | undefined => {
    if (!baseUrl) {
      return undefined;
    }
    const copilotApi = copilotExecutionApi(baseUrl);
    const sessionsApi = agentExecutionApi(baseUrl);
    return {
      invoke: async (copilotInvokeRequest) =>
        (
          await copilotApi.invokeCopilotCopilotApiV2InvokePost({
            copilotInvokeRequest,
          })
        ).data,
      listSessions: async (params) =>
        (
          await sessionsApi.listSessionsCopilotApiV2AgentsAgentIdSessionsGet({
            agentId: UNIFIED_COPILOT_AGENT_ID,
            environment: Environment.PRODUCTION,
            from: params.from,
            to: params.to,
            pageSize: params.pageSize,
            sortField: SortField.STARTED_AT,
            sortDirection: SortDirection.DESC,
            cursor: params.cursor,
            filterCreatedById: params.filterCreatedById,
          })
        ).data,
      listSessionMessages: async (params) =>
        (
          await sessionsApi.listSessionMessagesCopilotApiV2SessionsSessionIdMessagesGet(
            params,
          )
        ).data,
      cancelSession: async (params) => {
        await sessionsApi.cancelSessionCopilotApiV2SessionsSessionIdCancelPost(
          params,
        );
      },
    };
  }, [baseUrl]);

  return { client, workspaceId: workspace?.id ?? null };
};

type UseUnifiedCopilotSessionsOptions = {
  enabled?: boolean;
};

export const useUnifiedCopilotSessions = (
  options: UseUnifiedCopilotSessionsOptions = {},
) => {
  const { enabled = true } = options;
  const { client, workspaceId } = useUnifiedCopilotClient();
  const userId = useCurrentUserId();
  const to = endOfDay(new Date()).toISOString();
  const query = useInfiniteQuery({
    queryKey: unifiedCopilotSessionKeys.historyList(workspaceId, userId, to),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AgentHistoryResponse> => {
      if (!client) {
        throw new Error("Unified Copilot client not available");
      }
      if (userId === undefined) {
        throw new Error("Current user not available");
      }

      return client.listSessions({
        filterCreatedById: userId,
        from: SESSION_HISTORY_FROM,
        to,
        pageSize: SESSION_HISTORY_PAGE_SIZE,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled:
      enabled &&
      client !== undefined &&
      workspaceId !== null &&
      userId !== undefined,
    // The history dropdown renders its own error and retry state.
    throwOnError: false,
  });

  return {
    ...query,
    sessions: query.data?.pages.flatMap((page) => page.sessions) ?? [],
    totalCount: query.data?.pages[0]?.total_count ?? 0,
  };
};

const fetchUnifiedCopilotMessages = async ({
  client,
  sessionId,
  pageParam,
}: {
  client: UnifiedCopilotClient;
  sessionId: string;
  pageParam?: string;
}): Promise<UnifiedCopilotMessagesPage> => {
  try {
    return await client.listSessionMessages({
      sessionId,
      pageSize: MESSAGE_PAGE_SIZE,
      cursor: pageParam,
    });
  } catch (error) {
    // Session IDs are client-generated, so the first read can precede creation.
    if (!pageParam && isAxiosError(error) && error.response?.status === 404) {
      return { messages: [] };
    }
    throw error;
  }
};

// Polling can briefly lag behind the optimistic cache. Keep the local turn
// until the API returns an additional persisted copy of the same message.
const preserveOptimisticMessages = (
  data: UnifiedCopilotMessagesPage,
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): UnifiedCopilotMessagesPage => {
  const cached = queryClient.getQueryData<UnifiedCopilotMessagesData>(queryKey);
  const firstPage = cached?.pages[0];
  const cachedMessages = firstPage?.messages ?? [];
  const serverMessages = data.messages ?? [];

  if (!firstPage || cachedMessages.length === 0) {
    return data;
  }

  const optimisticMessage = cachedMessages[0];
  const optimisticContentBlocks =
    optimisticMessage?.type === HumanMessageTypeEnum.HUMAN &&
    optimisticMessage.id == null &&
    optimisticMessage.content_blocks?.length
      ? optimisticMessage.content_blocks
      : null;

  if (serverMessages.length > 0 && optimisticContentBlocks === null) {
    return data;
  }

  if (optimisticContentBlocks !== null) {
    const isServerEcho = (
      message: SessionMessagesResponseMessagesInner,
    ): boolean =>
      message.type === HumanMessageTypeEnum.HUMAN &&
      message.id != null &&
      isEqual(
        normalizeHumanContentBlocks(message.content_blocks),
        normalizeHumanContentBlocks(optimisticContentBlocks),
      );

    const hasNewServerEcho =
      serverMessages.filter(isServerEcho).length >
      cachedMessages.filter(isServerEcho).length;

    if (hasNewServerEcho) {
      return data;
    }
  }

  // Until the echo arrives, a terminal status can still belong to the prior turn.
  const latestActiveSession =
    data.session && !isTerminalStatus(data.session.status)
      ? data.session
      : firstPage.session;

  return {
    ...firstPage,
    session: latestActiveSession,
    next_cursor: data.next_cursor,
  };
};

type UseUnifiedCopilotMessagesOptions = {
  enabled?: boolean;
  pollingInterval?: number;
  awaitingSessionRef?: React.MutableRefObject<boolean>;
};

export const useUnifiedCopilotMessages = (
  sessionId: string | null,
  options?: UseUnifiedCopilotMessagesOptions,
) => {
  const {
    enabled = true,
    pollingInterval = 1000,
    awaitingSessionRef,
  } = options ?? {};
  const { client, workspaceId } = useUnifiedCopilotClient();
  const queryClient = useQueryClient();
  const queryKey = unifiedCopilotSessionKeys.messages(workspaceId, sessionId);

  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({
      pageParam,
    }: {
      pageParam?: string;
    }): Promise<UnifiedCopilotMessagesPage> => {
      if (!client) {
        throw new Error("Unified Copilot client not available");
      }
      if (!sessionId) {
        throw new Error("Unified Copilot session ID not available");
      }

      const data = await fetchUnifiedCopilotMessages({
        client,
        sessionId,
        pageParam,
      });

      return pageParam
        ? data
        : preserveOptimisticMessages(data, queryClient, queryKey);
    },
    getNextPageParam: (lastPage: UnifiedCopilotMessagesPage) =>
      lastPage.next_cursor ?? undefined,
    enabled:
      enabled &&
      client !== undefined &&
      workspaceId !== null &&
      sessionId !== null,
    refetchInterval: (currentQuery) => {
      if (currentQuery.state.status === "error") return false;

      const sessionStatus = currentQuery.state.data?.pages[0]?.session?.status;

      // Before the backend exposes the session, poll only after invoke dispatch.
      if (sessionStatus === undefined) {
        return awaitingSessionRef?.current ? pollingInterval : false;
      }

      if (awaitingSessionRef) awaitingSessionRef.current = false;
      return isTerminalStatus(sessionStatus) ? false : pollingInterval;
    },
    throwOnError: false,
  });

  // The API pages newest-first; chat renders messages chronologically.
  const messages: SessionMessagesResponseMessagesInner[] = (
    query.data?.pages.flatMap((page) => page.messages ?? []) ?? []
  ).toReversed();

  return {
    ...query,
    messages,
    queryKey,
    session: query.data?.pages[0]?.session,
  };
};

const invocationText = (request: CopilotInvokeRequest): string =>
  request.content_blocks
    ?.map((block) => {
      switch (block.type) {
        case "text":
          return block.text;
        case "mention":
          return block.label;
        // Files carry no inline text; they render as attachment cards.
        case "file":
          return "";
      }
    })
    .join("") ?? "";

export const isRetriableInvokeError = (error: Error): boolean =>
  isAxiosError(error) &&
  (error.response === undefined ||
    error.response.status === 409 ||
    error.response.status >= 500);

type UseUnifiedCopilotInvokeParams = {
  messagesQueryKey: readonly unknown[];
  onInvokeStarted?: (sessionId: CopilotInvokeRequest["session_id"]) => void;
  /**
   * Transparent retry: React Query retries the mutation before onError fires,
   * so the optimistic message stays in place across attempts.
   */
  retry?: (failureCount: number, error: Error) => boolean;
  retryDelay?: number;
};

type UnifiedCopilotMutationContext = {
  previousData: UnifiedCopilotMessagesData | undefined;
};

export const useUnifiedCopilotInvoke = ({
  messagesQueryKey,
  onInvokeStarted,
  retry,
  retryDelay,
}: UseUnifiedCopilotInvokeParams) => {
  const { client, workspaceId } = useUnifiedCopilotClient();
  const queryClient = useQueryClient();

  return useMutation<
    AgentInvokeResponse,
    Error,
    CopilotInvokeRequest,
    UnifiedCopilotMutationContext
  >({
    retry,
    retryDelay,
    mutationFn: async (
      copilotInvokeRequest: CopilotInvokeRequest,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Unified Copilot client not available");
      }

      return client.invoke(copilotInvokeRequest);
    },
    onMutate: async (request: CopilotInvokeRequest) => {
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });

      const previousData =
        queryClient.getQueryData<UnifiedCopilotMessagesData>(messagesQueryKey);
      const message = invocationText(request);
      const contentBlocks = request.content_blocks ?? [];

      queryClient.setQueryData<UnifiedCopilotMessagesData>(
        messagesQueryKey,
        (old) => {
          const firstPage = old?.pages[0];
          const optimisticPage: UnifiedCopilotMessagesPage = {
            ...firstPage,
            // Remove the previous turn's terminal status before polling restarts.
            session: undefined,
            messages: [
              {
                type: HumanMessageTypeEnum.HUMAN,
                content: message,
                content_blocks: contentBlocks,
                token_count: 0,
              } as SessionMessagesResponseMessagesInner,
              ...(firstPage?.messages ?? []),
            ],
          };

          return {
            pages: [optimisticPage, ...(old?.pages.slice(1) ?? [])],
            pageParams: old?.pageParams ?? [undefined],
          };
        },
      );

      return { previousData };
    },
    onSuccess: async (_response, request) => {
      // Invoke only acknowledges dispatch; message history carries the result.
      onInvokeStarted?.(request.session_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: messagesQueryKey }),
        queryClient.invalidateQueries({
          queryKey: unifiedCopilotSessionKeys.history(workspaceId),
        }),
      ]);
    },
    onError: (_error, _request, context) => {
      if (context?.previousData === undefined) {
        queryClient.removeQueries({
          queryKey: messagesQueryKey,
          exact: true,
        });
      } else {
        queryClient.setQueryData(messagesQueryKey, context.previousData);
      }

      toastActions.failure({
        title: "Failed to send message",
        description: "Please try again",
      });
    },
  });
};

type UseUnifiedCopilotCancelParams = {
  messagesQueryKey: readonly unknown[];
};

/**
 * Cancels the in-flight copilot session via the backend cancel endpoint.
 *
 * The endpoint is synchronous and may block several seconds while the backend
 * cleans up in-flight flow tools; callers derive an optimistic "stopping"
 * state from the mutation so the UI reacts instantly. When the call returns,
 * the session row is already `cancelled` — invalidating the messages query
 * snaps the polled status instead of waiting for the next poll tick.
 */
export const useUnifiedCopilotCancel = ({
  messagesQueryKey,
}: UseUnifiedCopilotCancelParams) => {
  const { client } = useUnifiedCopilotClient();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sessionId: string }>({
    mutationFn: async ({ sessionId }) => {
      if (!client) {
        throw new Error("Unified Copilot client not available");
      }

      try {
        await client.cancelSession({ sessionId });
      } catch (error) {
        // 409 = the run reached a terminal state first; nothing left to stop.
        if (isAxiosError(error) && error.response?.status === 409) {
          return;
        }
        throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: messagesQueryKey });
    },
    onError: () => {
      toastActions.failure({
        title: "Failed to stop Copilot",
        description: "Please try again",
      });
    },
  });
};
